import React, { useEffect, useState } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Box, xcss } from '@atlaskit/primitives';
import { toProjectType } from '@atlassian/jira-common-constants/src/project-types';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import { fg } from '@atlassian/jira-feature-gating';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireOperationalAnalytics,
	SCREEN,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import { VideoPlayerModal } from '@atlassian/video-player-modal';
import {
	API_FAIL,
	PACKAGE_NAME,
	ROVO_LOOM_VIDEO,
	SUBJECT_FEATURE_GATE_ID,
	TEAM_NAME,
} from './constants';
import messages from './messages';
import { getRovoVideoOnboardingHash, setRovoVideoOnboardingSeen } from './services';
import { fetchRovoEnablement } from './services/fetchRovoEnablement';
import { isJiraSoftware } from './utils';

export const RovoVideoModal = () => {
	const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false);

	const { atlassianAccountId, cloudId } = useTenantContext();
	const { loading: loadingProject, data: project } = useProject();
	const projectType = toProjectType(project?.type);

	const isGateEnabled = fg('rovo_video_player_gate');

	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		const checkOnboardingSeen = async () => {
			const hasSeenRovoOnboarding = await getRovoVideoOnboardingHash(atlassianAccountId);
			const { isRovoEnabled } = await fetchRovoEnablement(cloudId);
			if (
				!loadingProject &&
				isJiraSoftware(projectType) &&
				isGateEnabled &&
				isRovoEnabled &&
				!hasSeenRovoOnboarding
			) {
				setIsVideoModalOpen(true);
			}
		};
		checkOnboardingSeen();
	}, [atlassianAccountId, cloudId, isGateEnabled, loadingProject, projectType]);

	const handleClose = async () => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'rovoVideoModalCloseButton',
		);

		await setRovoVideoOnboardingSeen(atlassianAccountId, 'true');
		setIsVideoModalOpen(false);
	};

	return (
		<>
			{isVideoModalOpen && (
				<ContextualAnalyticsData sourceType={SCREEN} sourceName="rovoVideoModal">
					<JSErrorBoundary
						id={SUBJECT_FEATURE_GATE_ID}
						fallback="unmount"
						onError={(err: string | undefined) => {
							fireOperationalAnalytics(
								createAnalyticsEvent({
									actionSubject: SUBJECT_FEATURE_GATE_ID,
									action: API_FAIL,
								}),
								{ error: err },
							);
						}}
						teamName={TEAM_NAME}
						packageName={PACKAGE_NAME}
					>
						<Box xcss={ContainerStyles}>
							<VideoPlayerModal
								onClose={handleClose}
								title={messages.videoModalTitle}
								url={ROVO_LOOM_VIDEO}
							/>
						</Box>
					</JSErrorBoundary>
					<FireScreenAnalytics />
				</ContextualAnalyticsData>
			)}
		</>
	);
};

const ContainerStyles = xcss({
	position: 'fixed',
	right: '12px',
	bottom: '12px',
	zIndex: 'modal',
});
